
import React, { useState, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import FileViewerModal from "../../../Components/FileViewer/FileViewerModal";
import toast from "react-hot-toast";
import ItemService from "../../../services/supplier_appCommonService";

const AuctionTermsAcknowledgeModal = ({
  show,
  onHide,
  onAcknowledge,
  auctionNr,
  termsText,
  documentId,
  firstQuote,
  firstQuoteBy,
  items = [],
  itemQuantities = {},
  currency = "INR",
  fractions = 2,
  auctionId,
  supplierId,
}) => {
  const [agreed, setAgreed] = useState(false);
  const [showDoc, setShowDoc] = useState(false);
  const [itemPrices, setItemPrices] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [quotesSubmitted, setQuotesSubmitted] = useState(false);

  const isFQSupplier = firstQuote === true && firstQuoteBy === "Supplier";
  const decimals = parseInt(fractions) || 2;
  
  // Reset state when modal opens
  useEffect(() => {
    if (show) {
      setAgreed(false);
      setItemPrices({});
      setQuotesSubmitted(false);
      setSubmitting(false);
    }
  }, [show]);

  const handlePriceChange = (localId, value) => {
    if (/^\d*\.?\d*$/.test(value) || value === "") {
      setItemPrices((prev) => ({ ...prev, [localId]: value }));
    }
  };

  const allPricesFilled = isFQSupplier
    ? items.every((item) => {
        const val = parseFloat(itemPrices[item.local_id]);
        return !isNaN(val) && val > 0;
      })
    : true;

  const handleSubmitQuotes = async () => {
    for (const item of items) {
      const val = parseFloat(itemPrices[item.local_id]);
      if (isNaN(val) || val <= 0) {
        toast.error(`Please enter a valid price for: ${item.itemCode || item.itemNo || item.local_id}`, {
          position: "top-right",
        });
        return;
      }
    }

    setSubmitting(true);
    try {
      const bidItems = items.map((item) => ({
        local_id: item.local_id,
        itemCode: item.itemCode || "",
        itemDesc: item.itemDescription || item.itemNo || "",
        bidAmount: parseFloat(parseFloat(itemPrices[item.local_id]).toFixed(decimals)),
        uom: item.uom || "",
        quantity: parseFloat(itemQuantities?.[item.local_id]) || 1,
        currency: currency,
        lotNumber: null,
        serialNumber: null,
      }));

      const totalBid = bidItems.reduce(
        (sum, i) => sum + i.bidAmount * i.quantity,
        0
      );

      const res = await ItemService.auctionPlaceBid({
        auctionId,           
        supplierCode: supplierId,  
        supplierName: "",
        items: bidItems,     
        totalBid: parseFloat(totalBid.toFixed(decimals)), 
        isInitialQuote: true,
        bidTime: new Date().toISOString(),
        singleItemBid: false,
      });

      if (res?.status === 200) {
        toast.success("Initial quote submitted successfully!", {
          position: "top-right",
        });
        setQuotesSubmitted(true);
      } else {
        toast.error(res?.message || "Failed to submit quote. Try again.", {
          position: "top-right",
        });
      }
    } catch (err) {
      toast.error("Something went wrong while submitting quote.", {
        position: "top-right",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Can acknowledge only if:
  // FQ+Supplier: agreed + quotes submitted
  //  Normal: agreed only
  const canAcknowledge = isFQSupplier ? agreed && quotesSubmitted : agreed;

  return (
    <>
      <Modal show={show} onHide={onHide} size="lg" centered backdrop="static">
        <Modal.Header closeButton style={{ borderBottom: "2px solid #e2e8f0" }}>
          <Modal.Title style={{ fontSize: "15px", fontWeight: 700, color: "#1e293b" }}>
            <i className="bi bi-shield-check me-2" style={{ color: "#0f766e" }}></i>
            Terms &amp; Conditions — {auctionNr || "Auction"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ padding: "20px 24px" }}>

          {/* ── FQ Info Banner ── */}
          {isFQSupplier && (
            <div style={{
              background: "#eef2ff",
              border: "1.5px solid #c7d2fe",
              borderRadius: "8px",
              padding: "14px 16px",
              marginBottom: "16px",
              fontSize: "13px",
              color: "#3730a3",
              display: "flex",
              gap: "12px",
              alignItems: "flex-start",
            }}>
              <span style={{ fontSize: "22px", lineHeight: 1, marginTop: "1px" }}>📋</span>
              <div>
                <div style={{ fontWeight: 700, fontSize: "14px", marginBottom: "5px" }}>
                  Initial Quote Required
                </div>
                <div style={{ fontWeight: 400, lineHeight: 1.7, color: "#4338ca" }}>
                  Please enter your <strong>initial price</strong> for each item below,
                  then accept the Terms &amp; Conditions to enter the Live Auction.
                </div>
                {/* Step indicators */}
                <div style={{ marginTop: "10px", display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
                  {[
                    { label: "Step 1 — Enter Initial Quote", done: allPricesFilled },
                    { label: "Step 2 — Submit Quote", done: quotesSubmitted },
                    { label: "Step 3 — Accept T&C & Enter", done: canAcknowledge },
                  ].map((step, i) => (
                    <React.Fragment key={i}>
                      <span style={{
                        background: step.done ? "#dcfce7" : "#e0e7ff",
                        border: `1px solid ${step.done ? "#86efac" : "#a5b4fc"}`,
                        borderRadius: "20px",
                        padding: "3px 12px",
                        fontSize: "11px",
                        fontWeight: 700,
                        color: step.done ? "#065f46" : "#3730a3",
                      }}>
                        {step.done ? "✅" : "⏳"} {step.label}
                      </span>
                      {i < 2 && <span style={{ fontSize: "12px", color: "#6366f1" }}>→</span>}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/*  T&C Text  */}
          <div style={{
            background: "#f8fafc",
            border: "1px solid #e2e8f0",
            borderRadius: "6px",
            padding: "16px",
            maxHeight: "180px",
            overflowY: "auto",
            fontSize: "13px",
            lineHeight: 1.8,
            color: "#374151",
            marginBottom: "16px",
            whiteSpace: "pre-wrap",
          }}>
            {termsText || "No terms and conditions were provided for this auction."}
          </div>

          {/*  View Document Button  */}
          {documentId && (
            <div style={{ marginBottom: "16px" }}>
              <button
                className="btnTable"
                onClick={() => setShowDoc(true)}
                style={{ fontSize: "13px" }}
              >
                <i className="bi bi-file-earmark-text me-1"></i>
                View Attached Document
              </button>
            </div>
          )}

          {/*  Initial Quote Table (only FQ+Supplier)  */}
          {isFQSupplier && items.length > 0 && (
            <div style={{
              border: "1.5px solid #c7d2fe",
              borderRadius: "8px",
              overflow: "hidden",
              marginBottom: "16px",
            }}>
              {/* Table Header */}
              <div style={{
                background: "#eef2ff",
                padding: "10px 16px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                borderBottom: "1px solid #c7d2fe",
              }}>
                <span style={{ fontSize: "14px" }}>📋</span>
                <span style={{ fontWeight: 700, fontSize: "13px", color: "#3730a3" }}>
                  Enter Initial Quote for Each Item
                </span>
                {quotesSubmitted && (
                  <span style={{
                    marginLeft: "auto",
                    background: "#dcfce7",
                    border: "1px solid #86efac",
                    borderRadius: "20px",
                    padding: "2px 10px",
                    fontSize: "12px",
                    fontWeight: 700,
                    color: "#065f46",
                  }}>
                    ✅ Quotes Submitted
                  </span>
                )}
              </div>

              {/* Items Table */}
              <table style={{ width: "100%", borderCollapse: "collapse", background: "#fff" }}>
                <thead>
                  <tr style={{ background: "#f9fafb", borderBottom: "1px solid #e5e7eb" }}>
                    <th style={{ padding: "10px 16px", textAlign: "left", fontSize: "11.5px", fontWeight: 700, color: "#6b7280", textTransform: "uppercase" }}>#</th>
                    <th style={{ padding: "10px 16px", textAlign: "left", fontSize: "11.5px", fontWeight: 700, color: "#6b7280", textTransform: "uppercase" }}>Item Code</th>
                    <th style={{ padding: "10px 16px", textAlign: "left", fontSize: "11.5px", fontWeight: 700, color: "#6b7280", textTransform: "uppercase" }}>Description</th>
                    <th style={{ padding: "10px 16px", textAlign: "center", fontSize: "11.5px", fontWeight: 700, color: "#6b7280", textTransform: "uppercase" }}>Qty</th>
                    <th style={{ padding: "10px 16px", textAlign: "right", fontSize: "11.5px", fontWeight: 700, color: "#6b7280", textTransform: "uppercase" }}>
                      Your Price ({currency}) *
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, idx) => {
                    const qty = itemQuantities?.[item.local_id] || 1;
                    const price = parseFloat(itemPrices[item.local_id]);
                    const isValid = !isNaN(price) && price > 0;
                    return (
                      <tr key={item.local_id} style={{
                        borderBottom: idx < items.length - 1 ? "1px solid #f3f4f6" : "none",
                        background: quotesSubmitted ? "#f0fdf4" : "#fff",
                      }}>
                        <td style={{ padding: "12px 16px", color: "#9ca3af", fontSize: "13px" }}>
                          {idx + 1}
                        </td>
                        <td style={{ padding: "12px 16px" }}>
                          <div style={{ fontWeight: 700, fontSize: "13px", color: "#111827" }}>
                            {item.itemCode || item.itemNo || `Item ${item.local_id}`}
                          </div>
                        </td>
                        <td style={{ padding: "12px 16px" }}>
                          <div style={{ fontSize: "12px", color: "#6b7280" }}>
                            {item.itemDescription || "--"}
                          </div>
                        </td>
                        <td style={{ padding: "12px 16px", textAlign: "center", fontSize: "13px", color: "#374151" }}>
                          {qty} {item.uom || ""}
                        </td>
                        <td style={{ padding: "12px 16px", textAlign: "right" }}>
                          {quotesSubmitted ? (
                            <span style={{ fontWeight: 700, color: "#059669", fontSize: "14px" }}>
                              {currency} {parseFloat(itemPrices[item.local_id]).toLocaleString("en-IN", {
                                minimumFractionDigits: decimals,
                                maximumFractionDigits: decimals,
                              })}
                            </span>
                          ) : (
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "6px" }}>
                              <input
                                type="number"
                                min="0"
                                step={Math.pow(10, -decimals)}
                                value={itemPrices[item.local_id] || ""}
                                onChange={(e) => handlePriceChange(item.local_id, e.target.value)}
                                placeholder="0.00"
                                style={{
                                  width: "120px",
                                  padding: "7px 10px",
                                  border: `1.5px solid ${isValid ? "#86efac" : "#c7d2fe"}`,
                                  borderRadius: "6px",
                                  fontSize: "13px",
                                  textAlign: "right",
                                  outline: "none",
                                  background: isValid ? "#f0fdf4" : "#fafafe",
                                  color: "#111827",
                                }}
                              />
                              {isValid && (
                                <span style={{ color: "#059669", fontSize: "14px" }}>✓</span>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Submit Quotes Button */}
              {!quotesSubmitted && (
                <div style={{
                  padding: "12px 16px",
                  background: "#f9fafb",
                  borderTop: "1px solid #e5e7eb",
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "10px",
                  alignItems: "center",
                }}>
                  {!allPricesFilled && (
                    <span style={{ fontSize: "12px", color: "#ef4444", fontStyle: "italic" }}>
                      * Please enter price for all items
                    </span>
                  )}
                  <button
                    className="btnTable"
                    onClick={handleSubmitQuotes}
                    disabled={!allPricesFilled || submitting}
                    style={{
                      background: allPricesFilled ? "#6366f1" : "#9ca3af",
                      color: "#fff",
                      cursor: allPricesFilled ? "pointer" : "not-allowed",
                      opacity: allPricesFilled ? 1 : 0.7,
                      fontSize: "13px",
                      padding: "8px 18px",
                    }}
                  >
                    {submitting ? (
                      <>⏳ Submitting...</>
                    ) : (
                      <><i className="bi bi-send me-1"></i> Submit Initial Quote</>
                    )}
                  </button>
                </div>
              )}
            </div>
          )}

          {/*  Acknowledge Checkbox  */}
          <label style={{
            display: "flex",
            alignItems: "flex-start",
            gap: "12px",
            cursor: isFQSupplier && !quotesSubmitted ? "not-allowed" : "pointer",
            padding: "14px",
            background: agreed ? "#f0fdf4" : "#fff7ed",
            border: `1.5px solid ${agreed ? "#86efac" : "#fcd34d"}`,
            borderRadius: "8px",
            fontSize: "13px",
            color: "#374151",
            transition: "all 0.2s",
            opacity: isFQSupplier && !quotesSubmitted ? 0.5 : 1,
          }}>
            <input
              type="checkbox"
              checked={agreed}
              disabled={isFQSupplier && !quotesSubmitted}
              onChange={(e) => setAgreed(e.target.checked)}
              style={{
                marginTop: "2px",
                width: "16px",
                height: "16px",
                cursor: isFQSupplier && !quotesSubmitted ? "not-allowed" : "pointer",
                accentColor: "#0f766e",
                flexShrink: 0,
              }}
            />
            <span>
              I have read and agree to the above Terms &amp; Conditions for this
              auction. I understand the bidding rules and accept all participation
              requirements.
              {isFQSupplier && (
                <span style={{ display: "block", marginTop: "6px", color: "#4338ca", fontWeight: 600 }}>
                  I also confirm that my initial quotes are accurate and binding.
                </span>
              )}
              {isFQSupplier && !quotesSubmitted && (
                <span style={{ display: "block", marginTop: "4px", color: "#ef4444", fontSize: "12px", fontStyle: "italic" }}>
                  ⚠ Please submit your initial quote first to enable this checkbox.
                </span>
              )}
            </span>
          </label>

        </Modal.Body>

        <Modal.Footer style={{ borderTop: "1px solid #e2e8f0", gap: "8px" }}>
          <button
            className="btnTable"
            onClick={onHide}
            style={{ background: "#f3f4f6", color: "#374151" }}
          >
            Cancel
          </button>
          <button
            className="btnTable"
            onClick={() => { if (canAcknowledge) onAcknowledge(); }}
            disabled={!canAcknowledge}
            style={{
              background: canAcknowledge ? "#0f766e" : "#9ca3af",
              color: "#fff",
              cursor: canAcknowledge ? "pointer" : "not-allowed",
              opacity: canAcknowledge ? 1 : 0.75,
              transition: "all 0.2s",
            }}
          >
            <i className="bi bi-check-circle me-1"></i>
            {isFQSupplier ? "Acknowledge & Enter Live Auction" : "Acknowledge & Enter Live Auction"}
          </button>
        </Modal.Footer>
      </Modal>

      {/*  Document Viewer  */}
      <FileViewerModal
        show={showDoc}
        onHide={() => setShowDoc(false)}
        fileid={documentId}
      />
    </>
  );
};

export default AuctionTermsAcknowledgeModal;

